export type ScrollLogLevel = 'debug' | 'info' | 'warn' | 'error';

const LOG_PREFIX = '[Teo Widget SCROLL]';
const STORAGE_KEY = 'teo_widget_scroll_debug';

/** Embed / bootstrap fallback. localStorage always wins when set. */
let embedEnabled: boolean | null = null;
let sessionGeneration = 0;
let programmaticDepth = 0;

function readLocalStorageFlag(): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === '1' || stored === 'true') return true;
    if (stored === '0' || stored === 'false') return false;
  } catch {
    // ignore
  }
  return null;
}

function readScriptFlag(): boolean {
  if (typeof document === 'undefined') return false;
  const script = document.querySelector<HTMLScriptElement>(
    'script[data-teo-initialized][data-scroll-debug], script[data-slug][data-scroll-debug]',
  );
  return (
    script?.dataset.scrollDebug === 'true' || script?.dataset.scrollDebug === ''
  );
}

function readDebugFlag(): boolean {
  const stored = readLocalStorageFlag();
  if (stored !== null) return stored;
  if (embedEnabled !== null) return embedEnabled;
  return readScriptFlag();
}

/**
 * Set fallback from embed bootstrap (`data-scroll-debug` / env).
 * Does **not** write localStorage — live override:
 * `localStorage.setItem('teo_widget_scroll_debug', '1')`
 */
export function setScrollDebugEnabled(enabled: boolean): void {
  embedEnabled = enabled;
}

export function isScrollDebugEnabled(): boolean {
  return readDebugFlag();
}

export function bumpScrollSession(reason: string): number {
  sessionGeneration += 1;
  logScroll('session', reason, { generation: sessionGeneration });
  return sessionGeneration;
}

export function getScrollSessionGeneration(): number {
  return sessionGeneration;
}

export type ScrollMetrics = {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
  distance: number;
  pinned: boolean;
};

export function summarizeScroll(container: HTMLElement | null): ScrollMetrics | null {
  if (!container) return null;
  const { scrollTop, scrollHeight, clientHeight } = container;
  const distance = scrollHeight - scrollTop - clientHeight;
  return {
    scrollTop: Math.round(scrollTop),
    scrollHeight: Math.round(scrollHeight),
    clientHeight: Math.round(clientHeight),
    distance: Math.round(distance),
    pinned: distance <= 96,
  };
}

export function runProgrammaticScroll<T>(reason: string, fn: () => T): T {
  if (!readDebugFlag()) {
    programmaticDepth += 1;
    try {
      return fn();
    } finally {
      programmaticDepth -= 1;
    }
  }

  programmaticDepth += 1;
  logScroll('programmatic', `start: ${reason}`, { depth: programmaticDepth });
  try {
    return fn();
  } finally {
    programmaticDepth -= 1;
    logScroll('programmatic', `end: ${reason}`, { depth: programmaticDepth });
  }
}

export function isProgrammaticScroll(): boolean {
  return programmaticDepth > 0;
}

export function logScroll(
  scope: string,
  message: string,
  data?: unknown,
  level: ScrollLogLevel = 'info',
): void {
  if (!readDebugFlag()) return;

  const label = `${LOG_PREFIX} [${scope}] ${message}`;
  const payload = data === undefined ? undefined : [label, data];

  switch (level) {
    case 'debug':
      if (payload) console.debug(...payload);
      else console.debug(label);
      break;
    case 'warn':
      if (payload) console.warn(...payload);
      else console.warn(label);
      break;
    case 'error':
      if (payload) console.error(...payload);
      else console.error(label);
      break;
    default:
      if (payload) console.info(...payload);
      else console.info(label);
  }
}
